import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import Header from '../components/Header'
import {
  getStats, getUsers, getScans, getScanDetail,
  deleteScan, deleteScansBatch, getSettings,
  updateSetting, triggerCampaign, triggerMonthlyReport
} from '../lib/admin'

export default function AdminDashboard() {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('overview')
  const [messages, setMessages] = useState([])

  // Data states
  const [stats, setStats] = useState(null)
  const [users, setUsers] = useState([])
  const [userSearch, setUserSearch] = useState('')
  const [scans, setScans] = useState([])
  const [selectedScan, setSelectedScan] = useState(null)
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [config, setConfig] = useState({})
  const [loadingTab, setLoadingTab] = useState(false)

  const addMessage = (text, type = 'info') => {
    const id = Date.now()
    setMessages(prev => [...prev, { text, type, id }])
    setTimeout(() => setMessages(prev => prev.filter(m => m.id !== id)), 5000)
  }

  // Load data when tab changes
  useEffect(() => {
    setLoadingTab(true)
    const load = async () => {
      try {
        if (activeTab === 'overview') {
          setStats(await getStats())
        } else if (activeTab === 'users') {
          const data = await getUsers(userSearch)
          setUsers(data.users || [])
        } else if (activeTab === 'scans') {
          setScans(await getScans())
          setSelectedIds(new Set())
        } else if (activeTab === 'settings') {
          setConfig(await getSettings())
        }
      } catch (err) {
        addMessage(err.message, 'error')
      } finally {
        setLoadingTab(false)
      }
    }
    load()
  }, [activeTab])

  const handleUserSearch = async () => {
    try {
      const data = await getUsers(userSearch)
      setUsers(data.users || [])
    } catch (err) {
      addMessage('Failed to search users', 'error')
    }
  }

  const handleScanClick = async (id) => {
    try {
      setSelectedScan(await getScanDetail(id))
    } catch (err) {
      addMessage('Failed to load scan details', 'error')
    }
  }

  const handleDeleteScan = async (e, id) => {
    e.stopPropagation()
    if (!window.confirm('Delete this scan?')) return
    try {
      await deleteScan(id)
      setScans(prev => prev.filter(s => s.id !== id))
      if (selectedScan?.id === id) setSelectedScan(null)
      addMessage('Scan deleted', 'success')
    } catch (err) {
      addMessage('Failed to delete scan', 'error')
    }
  }

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return
    if (!window.confirm(`Delete ${selectedIds.size} scans?`)) return
    try {
      await deleteScansBatch(Array.from(selectedIds))
      setScans(prev => prev.filter(s => !selectedIds.has(s.id)))
      setSelectedIds(new Set())
      addMessage(`Deleted ${selectedIds.size} scans`, 'success')
    } catch (err) {
      addMessage('Failed to delete scans', 'error')
    }
  }

  const handleToggle = async (key) => {
    const newVal = config[key] === 'true' || config[key] === true ? false : true
    try {
      await updateSetting(key, newVal)
      setConfig(prev => ({ ...prev, [key]: newVal }))
      addMessage(`${key} set to ${newVal}`, 'success')
    } catch (err) {
      addMessage('Failed to update setting', 'error')
    }
  }

  const handleCampaign = async (type) => {
    if (!window.confirm(`Send "${type}" email campaign?`)) return
    try {
      const result = await triggerCampaign(type, 'ScamView Update')
      addMessage(`Campaign sent to ${result.sent}/${result.total} users`, 'success')
    } catch (err) {
      addMessage('Campaign failed: ' + err.message, 'error')
    }
  }

  const handleMonthlyReport = async () => {
    if (!window.confirm('Send monthly security report to all active users with activity?')) return
    try {
      const result = await triggerMonthlyReport()
      addMessage(`Monthly report sent to ${result.sent} users (${result.skipped} inactive skipped)`, 'success')
    } catch (err) {
      addMessage('Monthly report failed: ' + err.message, 'error')
    }
  }

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleSelectAll = () => {
    if (selectedIds.size === scans.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(scans.map(s => s.id)))
    }
  }

  const tabs = [
    { id: 'overview', label: 'Overview', color: 'sky' },
    { id: 'users', label: 'Users', color: 'sky' },
    { id: 'scans', label: 'Scans', color: 'sky' },
    { id: 'settings', label: 'Settings', color: 'sky' },
    { id: 'email', label: 'Email', color: 'sky' },
  ]

  return (
    <div className="min-h-screen bg-[#0a0e17]">
      <Header />

      <div className="container mx-auto px-4 py-12 max-w-6xl">
        <div className="flex items-center justify-between mb-8">
          <h1
            className="text-3xl font-bold text-white"
            style={{ fontFamily: "'Orbitron', sans-serif" }}
          >
            Admin Panel
          </h1>
          <button
            onClick={() => navigate('/dashboard')}
            className="btn-secondary text-sm"
          >
            &larr; Dashboard
          </button>
        </div>

        {/* Toast Messages */}
        <div className="fixed top-20 right-5 space-y-2 z-50">
          {messages.map(m => (
            <div
              key={m.id}
              className={`px-4 py-2 rounded-lg shadow-lg text-sm ${
                m.type === 'error' ? 'bg-red-600 text-white' :
                m.type === 'success' ? 'bg-green-600 text-white' :
                'bg-cyan-600 text-white'
              }`}
            >
              {m.text}
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-8 border-b border-white/10 overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-3 px-4 font-semibold transition whitespace-nowrap ${
                activeTab === tab.id
                  ? 'text-cyan-400 border-b-2 border-cyan-400'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {loadingTab && (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        )}

        {/* Overview Tab */}
        {activeTab === 'overview' && !loadingTab && stats && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="card text-center">
                <p className="text-3xl font-bold text-cyan-400">{stats.totalUsers}</p>
                <p className="text-gray-400 text-sm mt-1">Total Users</p>
              </div>
              <div className="card text-center">
                <p className="text-3xl font-bold text-emerald-400">{stats.totalScans}</p>
                <p className="text-gray-400 text-sm mt-1">Total Scans</p>
              </div>
              <div className="card text-center">
                <p className="text-3xl font-bold text-amber-400">{stats.recentScans}</p>
                <p className="text-gray-400 text-sm mt-1">Scans (7d)</p>
              </div>
              <div className="card text-center">
                <p className="text-3xl font-bold text-white">
                  {(stats.plans?.basic || 0) + (stats.plans?.premium || 0)}
                </p>
                <p className="text-gray-400 text-sm mt-1">Active Subs</p>
              </div>
            </div>

            <div className="card">
              <h3 className="text-white font-semibold mb-4">Plan Breakdown</h3>
              <div className="grid grid-cols-4 gap-4">
                {Object.entries(stats.plans || {}).map(([plan, count]) => (
                  <div key={plan} className="text-center">
                    <p className="text-xl font-bold text-white">{count}</p>
                    <p className="text-gray-500 text-sm capitalize">{plan}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && !loadingTab && (
          <div className="space-y-4">
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Search by email or username..."
                className="input flex-1"
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleUserSearch()}
              />
              <button onClick={handleUserSearch} className="btn-primary px-4">
                Search
              </button>
            </div>

            <div className="card overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10 text-gray-400">
                    <th className="text-left py-2 pr-4">Email</th>
                    <th className="text-left py-2 pr-4">Username</th>
                    <th className="text-left py-2 pr-4">Plan</th>
                    <th className="text-left py-2 pr-4">Role</th>
                    <th className="text-left py-2">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id} className="border-b border-white/5 hover:bg-white/5 text-gray-300">
                      <td className="py-2 pr-4">{u.email}</td>
                      <td className="py-2 pr-4">{u.username || '-'}</td>
                      <td className="py-2 pr-4">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          u.subscription_status === 'premium' ? 'bg-amber-500/10 text-amber-400' :
                          u.subscription_status === 'basic' ? 'bg-cyan-500/10 text-cyan-400' :
                          u.subscription_status === 'cancelled' ? 'bg-red-500/10 text-red-400' :
                          'bg-white/10 text-gray-400'
                        }`}>
                          {u.subscription_status || 'trial'}
                        </span>
                      </td>
                      <td className="py-2 pr-4">
                        {u.is_admin ? (
                          <span className="text-xs bg-red-500/10 text-red-400 px-2 py-0.5 rounded-full">Admin</span>
                        ) : (
                          <span className="text-gray-500">User</span>
                        )}
                      </td>
                      <td className="py-2 text-gray-500">
                        {new Date(u.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {users.length === 0 && (
                <p className="text-gray-500 text-center py-4">No users found.</p>
              )}
            </div>
          </div>
        )}

        {/* Scans Tab */}
        {activeTab === 'scans' && !loadingTab && (
          <div className="space-y-4">
            {selectedIds.size > 0 && (
              <div className="flex justify-end">
                <button
                  onClick={handleBulkDelete}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm transition"
                >
                  Delete Selected ({selectedIds.size})
                </button>
              </div>
            )}

            <div className="card overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10 text-gray-400">
                    <th className="py-2 pr-2 w-8">
                      <input
                        type="checkbox"
                        checked={scans.length > 0 && selectedIds.size === scans.length}
                        onChange={toggleSelectAll}
                        className="rounded"
                      />
                    </th>
                    <th className="text-left py-2 pr-4">Time</th>
                    <th className="text-left py-2 pr-4">User</th>
                    <th className="text-left py-2 pr-4">Type</th>
                    <th className="text-left py-2 pr-4">Target</th>
                    <th className="text-left py-2 pr-4">Risk</th>
                    <th className="text-right py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {scans.map(s => (
                    <tr
                      key={s.id}
                      className={`border-b border-white/5 cursor-pointer transition ${
                        selectedIds.has(s.id) ? 'bg-cyan-900/10' : 'hover:bg-white/5'
                      }`}
                      onClick={() => handleScanClick(s.id)}
                    >
                      <td className="py-2 pr-2" onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(s.id)}
                          onChange={() => toggleSelect(s.id)}
                          className="rounded"
                        />
                      </td>
                      <td className="py-2 pr-4 text-gray-500">
                        {new Date(s.created_at).toLocaleString()}
                      </td>
                      <td className="py-2 pr-4 text-gray-400">{s.email || 'Guest'}</td>
                      <td className="py-2 pr-4 capitalize text-gray-300">{s.target_type}</td>
                      <td className="py-2 pr-4 text-gray-300 max-w-[200px] truncate">{s.target_value}</td>
                      <td className={`py-2 pr-4 font-bold ${
                        s.risk_score >= 7 ? 'text-red-400' : s.risk_score >= 4 ? 'text-amber-400' : 'text-emerald-400'
                      }`}>
                        {s.risk_score}/10
                      </td>
                      <td className="py-2 text-right">
                        <button
                          onClick={(e) => handleDeleteScan(e, s.id)}
                          className="text-red-400 hover:text-red-300 transition text-xs"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {scans.length === 0 && (
                <p className="text-gray-500 text-center py-4">No scans found.</p>
              )}
            </div>
          </div>
        )}

        {/* Scan Detail Modal */}
        {selectedScan && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedScan(null)}
          >
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
            <div
              className="relative bg-[#111827] border border-white/10 rounded-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex justify-between items-center p-4 border-b border-white/10">
                <h3 className="font-bold text-white">Scan Details</h3>
                <button
                  onClick={() => setSelectedScan(null)}
                  className="text-gray-500 hover:text-white transition text-xl"
                >
                  &times;
                </button>
              </div>
              <div className="p-6 overflow-y-auto space-y-4">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                  <div className="bg-white/5 p-3 rounded-lg">
                    <div className="text-gray-500 text-xs uppercase mb-1">Target</div>
                    <div className="text-white font-mono text-sm truncate">{selectedScan.target_value}</div>
                  </div>
                  <div className="bg-white/5 p-3 rounded-lg">
                    <div className="text-gray-500 text-xs uppercase mb-1">Risk Score</div>
                    <div className={`font-bold ${
                      selectedScan.risk_score >= 7 ? 'text-red-400' : 'text-emerald-400'
                    }`}>{selectedScan.risk_score}/10</div>
                  </div>
                  <div className="bg-white/5 p-3 rounded-lg">
                    <div className="text-gray-500 text-xs uppercase mb-1">Type</div>
                    <div className="text-white capitalize">{selectedScan.target_type}</div>
                  </div>
                  <div className="bg-white/5 p-3 rounded-lg">
                    <div className="text-gray-500 text-xs uppercase mb-1">Time</div>
                    <div className="text-white text-sm">{new Date(selectedScan.created_at).toLocaleString()}</div>
                  </div>
                </div>

                {selectedScan.ai_summary && (
                  <div>
                    <h4 className="font-bold text-cyan-400 mb-2">AI Summary</h4>
                    <p className="text-gray-300 text-sm">{selectedScan.ai_summary}</p>
                  </div>
                )}

                {selectedScan.internal_analysis && (
                  <div>
                    <h4 className="font-bold text-cyan-400 mb-2">Internal Analysis</h4>
                    <pre className="bg-black/30 p-4 rounded-lg border border-white/5 text-xs overflow-x-auto text-emerald-400 font-mono">
                      {(() => {
                        try {
                          const parsed = typeof selectedScan.internal_analysis === 'string'
                            ? JSON.parse(selectedScan.internal_analysis)
                            : selectedScan.internal_analysis
                          return JSON.stringify(parsed, null, 2)
                        } catch {
                          return String(selectedScan.internal_analysis)
                        }
                      })()}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && !loadingTab && (
          <div className="card max-w-lg">
            <h3 className="text-xl font-bold text-white mb-4">Feature Toggles</h3>
            <p className="text-gray-400 text-sm mb-6">
              Enable or disable scanning engines in real-time.
            </p>
            <div className="space-y-4">
              {['enable_ai', 'enable_brand', 'enable_phone', 'enable_links'].map(key => {
                const isOn = config[key] === true || config[key] === 'true'
                return (
                  <div key={key} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                    <span className="text-gray-300 capitalize">{key.replace('enable_', '')} Engine</span>
                    <button
                      onClick={() => handleToggle(key)}
                      className={`w-12 h-6 rounded-full transition-colors relative ${
                        isOn ? 'bg-emerald-500' : 'bg-gray-600'
                      }`}
                    >
                      <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${
                        isOn ? 'left-7' : 'left-1'
                      }`} />
                    </button>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Email Tab */}
        {activeTab === 'email' && !loadingTab && (
          <div className="space-y-6 max-w-lg">
            <div className="card">
              <h3 className="text-white font-bold mb-2">Welcome Series</h3>
              <p className="text-gray-400 text-sm mb-4">
                Target: Members created in the last 7 days.
              </p>
              <button
                onClick={() => handleCampaign('new_members')}
                className="btn-primary text-sm"
              >
                Send Welcome Blast
              </button>
            </div>

            <div className="card">
              <h3 className="text-white font-bold mb-2">Monthly Newsletter</h3>
              <p className="text-gray-400 text-sm mb-4">
                Target: All active subscribers (trial, basic, premium).
              </p>
              <button
                onClick={() => handleCampaign('monthly')}
                className="btn-primary text-sm"
              >
                Send Newsletter
              </button>
            </div>

            <div className="card border-cyan-500/20">
              <h3 className="text-white font-bold mb-2">Monthly Security Report</h3>
              <p className="text-gray-400 text-sm mb-4">
                Sends personalized monthly summary to all active users: time saved, community scam detection rates, and emerging trends. Only users with activity receive the email.
              </p>
              <button
                onClick={() => handleMonthlyReport()}
                className="btn-primary text-sm"
              >
                Send Monthly Report
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
